import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import pageStyles from '../ManagementPages.module.css';
import { EditIcon } from '../../components/icons/Icons';
import { Row, Col, Button, Container, Form } from 'react-bootstrap';
import styles from './ReferenceCode.module.css';
import { Paginator } from 'primereact/paginator';
import gridStyles from '../../components/PerinHealthGrid.module.css';
import { updateRow, setSelectedRowId, setAlertsPriorityRows } from './AlertsPrioritySlice';
import useAxios from '../../services/AxiosConfig';
import EditAlertPriorityModal from './EditAlertPriorityModal';
import { formatDateString } from '../../utils/dateUtils';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';

const UpdateAlertsPriority = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);
    const formRef = useRef(null);
    const [validated, setValidated] = useState(false);
    const alertsPriorityData = useSelector((state) => state.alertsPriority);
    const [first, setFirst] = useState(0);
    const [rowCount, setRowCount] = useState(15);
    const [showEditModal, setShowEditModal] = useState(false);
    const userDateFormat = useSelector((state) => state.user.userDateFormat);
    
    // Filter state
    const [filters, setFilters] = useState({
        type: [],
        subType: [],
        priority: [],
        color: []
    });
    const [showFilterDropdown, setShowFilterDropdown] = useState({
        type: false,
        subType: false,
        priority: false,
        color: false
    });
    const filterDropdownRef = useRef({});

    const onPageChange = (event) => {
        setFirst(event.first);
        setRowCount(event.rows);
    };

    const handleEditClick = (row) => {
        dispatch(setSelectedRowId(row.AlertsPriority_id));
        setShowEditModal(true);
    };

    const handleEditClose = (refreshData = false) => {
        setShowEditModal(false);
        dispatch(setSelectedRowId(0));
        if (refreshData === true) {
            fetchAlertsPriority();
        }
    };

    const fetchAlertsPriority = async () => {
        try {
            const response = await axios.get('alertsPriority');
            if (response.data && response.data.data && Array.isArray(response.data.data)) {
                dispatch(setAlertsPriorityRows(response.data.data));
                setFirst(0);
            }
        } catch (error) {
            console.error("Error fetching alerts priority:", error);
        }
    };

    // Get unique values for each filter column (formatted for MultiSelectDropdown)
    const filterOptions = useMemo(() => {
        const types = [...new Set(alertsPriorityData.rows.map(row => row.AlertPriority_Type).filter(Boolean))];
        const subTypes = [...new Set(alertsPriorityData.rows.map(row => row.AlertPriority_AlertSubType).filter(Boolean))];
        const priorities = [...new Set(alertsPriorityData.rows.map(row => row.AlertPriority_PrioritySet).filter(Boolean))];
        const colors = [...new Set(alertsPriorityData.rows.map(row => row.AlertPriority_Color).filter(Boolean))];
        
        return { 
            types: types.map(type => ({ label: type, value: type })),
            subTypes: subTypes.map(subType => ({ label: subType, value: subType })),
            priorities: priorities.map(priority => ({ label: priority || 'None', value: priority || '' })),
            colors: colors.map(color => ({ label: color || 'None', value: color || '' }))
        };
    }, [alertsPriorityData.rows]);

    // Filter and paginate rows
    const filteredAndPaginatedRows = useMemo(() => {
        let filtered = alertsPriorityData.rows.filter(row => {
            if (filters.type.length > 0 && !filters.type.includes(row.AlertPriority_Type)) return false;
            if (filters.subType.length > 0 && !filters.subType.includes(row.AlertPriority_AlertSubType)) return false;
            if (filters.priority.length > 0 && !filters.priority.includes(row.AlertPriority_PrioritySet)) return false;
            if (filters.color.length > 0 && !filters.color.includes(row.AlertPriority_Color)) return false;
            return true;
        });
        return filtered.slice(first, first + rowCount);
    }, [alertsPriorityData.rows, filters, first, rowCount]);

    const totalFilteredCount = useMemo(() => {
        return alertsPriorityData.rows.filter(row => {
            if (filters.type.length > 0 && !filters.type.includes(row.AlertPriority_Type)) return false;
            if (filters.subType.length > 0 && !filters.subType.includes(row.AlertPriority_AlertSubType)) return false;
            if (filters.priority.length > 0 && !filters.priority.includes(row.AlertPriority_PrioritySet)) return false;
            if (filters.color.length > 0 && !filters.color.includes(row.AlertPriority_Color)) return false;
            return true;
        }).length;
    }, [alertsPriorityData.rows, filters]);

    // Handle filter toggle
    const toggleFilter = (filterType) => {
        setShowFilterDropdown(prev => ({
            ...prev,
            [filterType]: !prev[filterType]
        }));
    };

    // Handle filter selection - wrapper functions for MultiSelectDropdown
    // MultiSelectDropdown passes a single item, so we need to toggle it
    const handleTypeFilterChange = useCallback((item) => {
        setFilters(prev => {
            const currentValues = prev.type;
            const value = item.value || item;
            const newValues = currentValues.includes(value)
                ? currentValues.filter(v => v !== value)
                : [...currentValues, value];
            setFirst(0);
            return { ...prev, type: newValues };
        });
    }, []);

    const handleSubTypeFilterChange = useCallback((item) => {
        setFilters(prev => {
            const currentValues = prev.subType;
            const value = item.value || item;
            const newValues = currentValues.includes(value)
                ? currentValues.filter(v => v !== value)
                : [...currentValues, value];
            setFirst(0);
            return { ...prev, subType: newValues };
        });
    }, []);

    const handlePriorityFilterChange = useCallback((item) => {
        setFilters(prev => {
            const currentValues = prev.priority;
            const value = item.value || item || '';
            const newValues = currentValues.includes(value)
                ? currentValues.filter(v => v !== value)
                : [...currentValues, value];
            setFirst(0);
            return { ...prev, priority: newValues };
        });
    }, []);

    const handleColorFilterChange = useCallback((item) => {
        setFilters(prev => {
            const currentValues = prev.color;
            const value = item.value || item || '';
            const newValues = currentValues.includes(value)
                ? currentValues.filter(v => v !== value)
                : [...currentValues, value];
            setFirst(0);
            return { ...prev, color: newValues };
        });
    }, []);

    // Check selected items for MultiSelectDropdown
    // MultiSelectDropdown passes the checkedKey value (which is 'value' in our case)
    const checkSelectedTypeItem = useCallback((value) => {
        return filters.type.includes(value);
    }, [filters.type]);

    const checkSelectedSubTypeItem = useCallback((value) => {
        return filters.subType.includes(value);
    }, [filters.subType]);

    const checkSelectedPriorityItem = useCallback((value) => {
        return filters.priority.includes(value || '');
    }, [filters.priority]);

    const checkSelectedColorItem = useCallback((value) => {
        return filters.color.includes(value || '');
    }, [filters.color]);

    // Check if any filter is active
    const hasActiveFilters = filters.type.length > 0 || filters.subType.length > 0 || filters.priority.length > 0 || filters.color.length > 0;

    return (
        <div className={pageStyles.MainCard}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight:"20px" }}>
                <div className={pageStyles.ContentTitle}>{"Alerts Priority"}</div>
                <div style={{ display: "flex", gap: "10px", paddingTop: "20px" }}>
                    {hasActiveFilters && (
                        <button className={styles.ClearFiltersButton} onClick={() => setFilters({ type: [], subType: [], priority: [], color: [] })}>Clear Filters</button>
                    )}
                    <MultiSelectDropdown 
                        inputWidth='150px'
                        options={filterOptions.types}
                        label='label'
                        checkedKey='value'
                        isSearch={false}
                        checkSelectedItem={checkSelectedTypeItem}
                        handleChange={handleTypeFilterChange}
                        selectedItems={filters.type.map(val => ({ label: val, value: val }))}
                        name='Type'
                    />
                    <MultiSelectDropdown 
                        inputWidth='150px'
                        options={filterOptions.subTypes}
                        label='label'
                        checkedKey='value'
                        isSearch={false}
                        checkSelectedItem={checkSelectedSubTypeItem}
                        handleChange={handleSubTypeFilterChange}
                        selectedItems={filters.subType.map(val => ({ label: val, value: val }))}
                        name='Sub-Type'
                    />
                    <MultiSelectDropdown 
                        inputWidth='150px'
                        options={filterOptions.priorities}
                        label='label'
                        checkedKey='value'
                        isSearch={false}
                        checkSelectedItem={checkSelectedPriorityItem}
                        handleChange={handlePriorityFilterChange}
                        selectedItems={filters.priority.map(val => ({ label: val || 'None', value: val || '' }))}
                        name='Priority'
                    />
                    <MultiSelectDropdown 
                        inputWidth='150px'
                        options={filterOptions.colors}
                        label='label'
                        checkedKey='value'
                        isSearch={false}
                        checkSelectedItem={checkSelectedColorItem}
                        handleChange={handleColorFilterChange}
                        selectedItems={filters.color.map(val => ({ label: val || 'None', value: val || '' }))}
                        name='Color'
                    />
                </div>
            </div>
            <hr></hr>
            <Form ref={formRef} noValidate validated={validated} style={{ height: "100%", width: "100%", padding: "0 20px 20px 20px" }}>
                <Container className={styles.FormContainer}>
                    <Row className={styles.HeaderRowSticky}>
                        <Col xs={2}>Name</Col>
                        <Col xs={2}>Type</Col>
                        <Col xs={2}>Sub-Type</Col>
                        <Col>Display</Col>
                        <Col xs={1}>Priority</Col>
                        <Col xs={1}>Color</Col>
                        <Col xs={1}>Last Updated</Col>
                        <Col xs={1}></Col>
                    </Row>
                    <div className={styles.ScrollableRowsContainer}>
                        {filteredAndPaginatedRows.map((row, index) => {
                            return (
                                <Row className={styles.CodeRow} key={`${row.AlertsPriority_id}-${index}`}>
                                    <Col xs={2}>
                                        <Form.Group>
                                            <Form.Control
                                                disabled={true}
                                                className={styles.CodeInput}
                                                required
                                                value={row.AlertPriority_NotificationName}

                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={2}>
                                        <Form.Group>
                                            <Form.Control
                                                disabled={true}
                                                className={styles.CodeInput}
                                                required
                                                value={row.AlertPriority_Type}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={2}>
                                        <Form.Group>
                                            <Form.Control
                                                disabled={true}
                                                className={styles.CodeInput}
                                                required
                                                value={row.AlertPriority_AlertSubType}

                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group>
                                            <Form.Control
                                                disabled={true}
                                                className={styles.CodeInput}
                                                required
                                                value={row.AlertPriority_DisplayName_Business}

                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={1}>
                                        <Form.Group>
                                            <Form.Control
                                                disabled={true}
                                                className={styles.CodeInput}
                                                required
                                                value={row.AlertPriority_PrioritySet}

                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={1}>
                                        <Form.Group>
                                            <Form.Control
                                                disabled={true}
                                                className={styles.CodeInput}
                                                required
                                                value={row.AlertPriority_Color}
                                                onChange={(e) => dispatch(updateRow({ id: row.AlertsPriority_id, name: "name", value: e.target.value }))
                                                }
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col xs={1} style={{ display: "flex", alignItems: "center" }}>
                                        <span>
                                            {row.AlertPriority_lastupdated ? formatDateString(row.AlertPriority_lastupdated, userDateFormat) : 'N/A'}
                                        </span>
                                    </Col>


                                    <Col xs={1} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <Button
                                            className={styles.RemoveColButton}
                                            onClick={() => handleEditClick(row)}
                                            title="Edit"
                                        >
                                            <EditIcon />
                                        </Button>
                                    </Col>
                                </Row>
                            )
                        })}
                    </div>
                    <div className={styles.FormNavContainer}>
                        <span style={{
                            fontFamily: 'Roboto',
                            fontSize: '12px',
                            fontWeight: 500,
                            lineHeight: '16px',
                            textAlign: 'left',
                            color: '#25282B'
                        }}>Total Number: {totalFilteredCount} {hasActiveFilters && `(Filtered from ${alertsPriorityData.rows.length})`}</span>
                        <Paginator
                            first={first}
                            rows={rowCount}
                            totalRecords={totalFilteredCount}
                            onPageChange={onPageChange}
                            rowsPerPageOptions={[15, 25, 50, 100]}
                            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown "
                            className={gridStyles.customPaginator}
                        />
                    </div>
                </Container>
            </Form>
            {showEditModal && alertsPriorityData.selectedRowId !== 0 && (
                <EditAlertPriorityModal
                    show={showEditModal}
                    onHide={handleEditClose}
                />
            )}
        </div>
    );
}

export default UpdateAlertsPriority;
